import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { lockScroll, unlockScroll } from 'utils/scrollLock';
import './CookiePanel.css';

const CookiePanel = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [preferences, setPreferences] = useState(true);

  // Load saved prefs
  useEffect(() => {
    const saved = localStorage.getItem('phys_cookie_prefs');
    if (saved) {
      try {
        const prefs = JSON.parse(saved);
        setAnalytics(prefs.analytics ?? false);
        setMarketing(prefs.marketing ?? false);
        setPreferences(prefs.preferences ?? true);
      } catch (e) { /* ignore */ }
    }
  }, [isOpen]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      lockScroll();
    } else {
      unlockScroll();
    }
    return () => { unlockScroll(); };
  }, [isOpen]);

  const savePreferences = () => {
    const prefs = { essential: true, analytics, marketing, preferences };
    localStorage.setItem('phys_cookie_prefs', JSON.stringify(prefs));
    localStorage.setItem('cookieShowed', 'true');
    onClose();
  };

  const acceptAll = () => {
    setAnalytics(true);
    setMarketing(true);
    setPreferences(true);
    const prefs = { essential: true, analytics: true, marketing: true, preferences: true };
    localStorage.setItem('phys_cookie_prefs', JSON.stringify(prefs));
    localStorage.setItem('cookieShowed', 'true');
    onClose();
  };

  const rejectAll = () => {
    setAnalytics(false);
    setMarketing(false);
    setPreferences(false);
    const prefs = { essential: true, analytics: false, marketing: false, preferences: false };
    localStorage.setItem('phys_cookie_prefs', JSON.stringify(prefs));
    localStorage.setItem('cookieShowed', 'true');
    onClose();
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`cookie-overlay ${isOpen ? 'open' : ''}`}
        onClick={onClose}
      />

      {/* Sticky tab on right edge */}
      <button
        className={`cookie-tab ${isOpen ? 'hidden' : ''}`}
        onClick={onClose}
        style={{ display: 'none' }}
      >
        Cookies
      </button>

      {/* Slide panel */}
      <div
        className={`cookie-panel ${isOpen ? 'open' : ''}`}
        role="dialog"
        aria-label="Configuración de cookies"
      >
        <button className="cookie-panel-close" onClick={onClose} aria-label="Cerrar">
          ✕
        </button>

        <div className="cookie-panel-header">
          <h2>Configuración de cookies</h2>
          <p>
            Personaliza qué cookies aceptas. Las cookies esenciales siempre están activas para garantizar el funcionamiento del sitio.{' '}
            <button
              className="cookie-panel-policy-link"
              onClick={() => { onClose(); navigate('/policies?section=cookies'); }}
            >
              Ver política completa
            </button>.
          </p>
        </div>

        <div className="cookie-panel-body">
          {/* Esenciales */}
          <div className="cookie-category">
            <div className="cookie-cat-header">
              <span>Cookies esenciales</span>
              <label className="cookie-toggle">
                <input type="checkbox" checked disabled />
                <span className="cookie-toggle-slider" />
              </label>
            </div>
            <p className="cookie-cat-desc">
              Necesarias para que el sitio funcione correctamente: sesión de usuario, carrito de compra y preferencias básicas. No pueden desactivarse.
            </p>
          </div>

          {/* Analíticas */}
          <div className="cookie-category">
            <div className="cookie-cat-header">
              <span>Cookies analíticas</span>
              <label className="cookie-toggle">
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={e => setAnalytics(e.target.checked)}
                />
                <span className="cookie-toggle-slider" />
              </label>
            </div>
            <p className="cookie-cat-desc">
              Nos ayudan a entender cómo los visitantes interactúan con el sitio, qué páginas se visitan más y posibles errores de navegación.
            </p>
          </div>

          {/* Marketing */}
          <div className="cookie-category">
            <div className="cookie-cat-header">
              <span>Cookies de marketing</span>
              <label className="cookie-toggle">
                <input
                  type="checkbox"
                  checked={marketing}
                  onChange={e => setMarketing(e.target.checked)}
                />
                <span className="cookie-toggle-slider" />
              </label>
            </div>
            <p className="cookie-cat-desc">
              Permiten mostrar anuncios personalizados en otros sitios y redes sociales basados en tu comportamiento de navegación.
            </p>
          </div>

          {/* Preferencias */}
          <div className="cookie-category">
            <div className="cookie-cat-header">
              <span>Cookies de preferencias</span>
              <label className="cookie-toggle">
                <input
                  type="checkbox"
                  checked={preferences}
                  onChange={e => setPreferences(e.target.checked)}
                />
                <span className="cookie-toggle-slider" />
              </label>
            </div>
            <p className="cookie-cat-desc">
              Recuerdan tus ajustes personales como idioma, región y moneda para ofrecerte una experiencia más relevante en visitas futuras.
            </p>
          </div>
        </div>

        <div className="cookie-panel-footer">
          <button className="cookie-btn-primary" onClick={savePreferences}>
            Guardar preferencias
          </button>
          <div className="cookie-btn-row">
            <button className="cookie-btn-secondary" onClick={acceptAll}>
              Aceptar todas
            </button>
            <button className="cookie-btn-secondary" onClick={rejectAll}>
              Rechazar todas
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookiePanel;
